import { Injectable } from '@angular/core';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AuthenticateService } from '@fi-app/utilities/fi-services/authenticate.service';
import { DesktopUserCredentialModel } from '@fi-app/core/desktop-user-credential.model';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { FiInactivityCountdownNotificationService } from '../utilities/inactivity-countdown-notification/inactivity-countdown-notification.service';
import { InactivityCountdownHttpHandlerService } from '../utilities/fi-services/inactivity-countdown-http-handler.service';
import { StatusCodeType } from '@fi-shared/types/status-code-type';
import { AppTextService } from '@fi-shared/app-text';
import { windowOpenCentered } from '@fi-shared/fi-shared-services/extensions.service';
import { toUrlEncoded } from '@fi-shared/utils/to-url-encoded';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiLocaleService } from '@fi-shared/fi-locale.service';
import { IADAuthSessionOptions, IChainformationPlatformAdLoginData } from '@fi-shared/ad-integration/ad-integration';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { UserService } from '@fi-app/user/user.service';
import { FiUserDataService, IFiUserData } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { UserPreferencesService } from '@fi-shared/fi-shared-services/user-preferences.service';
import { ActionFailureReasonCode } from '@fi-shared/types/action-failed-reason-code';
import { ILoginData, ILoginSettings } from '@fi-shared/fi-auth-shared/fi-auth-shared';
import { FiAuthenticateSharedService } from '@fi-shared/fi-auth-shared/fi-authenticate-shared.service';
import { FiLoginType } from '@fi-shared/fi-auth-shared/fi-login-type';

@Injectable()
export class FiAuthenticateModuleService {
    constructor(
        private loadingService: LoadingService,
        private authService: AuthenticateService,
        private sessionService: SessionService,
        private contentLanguageService: ContentLanguagesService,
        private inactivityCountdownNotificationService: FiInactivityCountdownNotificationService,
        private inactivityCountdownHttpHandlerService: InactivityCountdownHttpHandlerService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private detectDevice: DetectDevicesService,
        private stateService: FiStateService,
        private localeService: FiLocaleService,
        private fiBackend: FiBackendService,
        private userService: UserService,
        private userDataService: FiUserDataService,
        private userPreferences: UserPreferencesService,
        private authenticateSharedService: FiAuthenticateSharedService,
    ) {
        this.authenticateSharedService.userLoginRetry$.subscribe((loginData) => {
            if (loginData.loginType === FiLoginType.DesktopLoginPassword) {
                // user changed his business unit to active one, accepted agreement or fixed inconsistent login/email contacts
                this.LoginWithCredentials(loginData);
            }
        });
    }

    LoginWithCredentials(loginData: ILoginData) {
        const userCredentials = loginData.userCredentials;
        this.loadingService.startLoading();

        this.authService.Login(userCredentials).then(
            (data) => {
                this.InitializeLoginData(data, { skipTokenCheck: true });

                this.userDataService.updateLoginRememberMe(
                    loginData.loginSettings.RememberMe ? userCredentials.Login : undefined,
                );
                this.userDataService.updateStayLoggedIn(userCredentials.ShouldStayLoggedIn);
            },
            (response) => {
                this.authenticateSharedService.processLoginError(response, loginData);
                this.loadingService.finishLoading();
            },
        );
    }

    InitializeLoginData(data: IFiUserData, args?: { skipTokenCheck: boolean }) {
        this.sessionService.resetSession();

        this.userDataService.setUserData(data);
        this.localeService.setLanguage(this.userDataService.applicationLanguage);

        this.userPreferences.clearListSort();

        const queue: Promise<any>[] = [];
        if (!args?.skipTokenCheck) {
            queue.push(this.authService.IsTokenValid(this.userDataService.getToken()));
        }
        queue.push(this.appTextService.loadApplicationLanguageLabels(this.userDataService.applicationLanguage, true));

        Promise.all(queue).then(() => {
            this.contentLanguageService.UpdateContentLanguageNames();
            this.sessionService.syncSession();

            this.inactivityCountdownNotificationService.ResetTimer();
            this.inactivityCountdownHttpHandlerService.Reset = () => {
                this.inactivityCountdownNotificationService.ResetTimer();
            };
        });

        this.stateService.go(FiStateNames.Home._name);
    }

    /*ForceLogout(userCredential, settings: ILoginWithCredentialSettings) {
        this.authService.ForceLogout(userCredential).then((data) => {
            this.InitializeLoginData(data);
        }, (response) => {
            this.loginErrorCallback(response, userCredential, settings)
        });
    };*/

    loginWithAd(options: IADAuthSessionOptions = {}) {
        this.loadingService.startLoading();
        const defaultOptions: IADAuthSessionOptions = {
            keepLoggedIn: false,
            isMobile: this.detectDevice.isMobilePhone(),
            forceLogout: false,
        };
        const loginData: IADAuthSessionOptions = Object.assign(
            {},
            this.authService.getBaseLoginParams(),
            defaultOptions,
            options,
        );
        const url = this.fiBackend.getRequestUrl(`ADIntegration/Login?${toUrlEncoded(loginData)}`);
        const newWindow = windowOpenCentered(url, 300, 400);
        if (window.focus) {
            newWindow.focus();

            const interval = setInterval(() => {
                if (newWindow.closed) {
                    this.loadingService.finishLoading();
                    clearInterval(interval);
                }
            }, 1000);
        }
    }

    processChainformationPlatformAdLogin(data: IChainformationPlatformAdLoginData) {
        if (data.UserData && data.StatusCode === StatusCodeType.Ok) {
            this.InitializeLoginData(data.UserData);
        } else {
            if (
                !this.authenticateSharedService.processLoginCommonErrorData({
                    error: data.ErrorData,
                    status: data.StatusCode,
                })
            ) {
                const reasonCode = data.ErrorData?.Reason;
                const errorBodyData = data?.ErrorData?.Data;
                switch (reasonCode) {
                    /*case ActionFailureReasonCode.UserAlreadyLoggedIn:
                        this.fiDialogsService.confirm({
                            messageText: this.appTextService.get('common.login_already_logged_in_message'),
                            headerText: this.appTextService.get('common.login_already_logged_in')})
                            .onYesButtonClick(() => {
                                this.loginWithAd(Object.assign({}, data.Payload, {forceLogout: true}));
                            });
                        break;*/
                    case ActionFailureReasonCode.User_LicenseAgreementMustAccept:
                        this.appTextService
                            .loadApplicationLanguageLabels(errorBodyData.UserPreferredContentLanguageId)
                            .then(() => {
                                this.stateService.go(FiStateNames.Login.LicenceAgreement, {
                                    UserPreferredContentLanguageId: errorBodyData.UserPreferredContentLanguageId,
                                    DefaultContentLanguageId: errorBodyData.DefaultContentLanguageId,
                                    adCredentials: data.Payload,
                                    agreementNotAcceptedResponse: errorBodyData,
                                });
                            });
                        return;
                    case ActionFailureReasonCode.User_ConfidentialAgreementMustAccept:
                        this.appTextService
                            .loadApplicationLanguageLabels(errorBodyData.UserPreferredContentLanguageId)
                            .then(() => {
                                this.stateService.go(FiStateNames.Login.ConfidentialAgreement, {
                                    UserPreferredContentLanguageId: errorBodyData.UserPreferredContentLanguageId,
                                    DefaultContentLanguageId: errorBodyData.DefaultContentLanguageId,
                                    adCredentials: data.Payload,
                                    agreementNotAcceptedResponse: errorBodyData,
                                });
                            });
                        return;
                    default:
                        this.fiDialogsService.error({
                            messageText: this.appTextService.get('common.login_error_temporary'),
                        });
                        break;
                }
            }
        }
    }
}
